import React from 'react'
import { Modal as AntdModal, Space, Spin } from 'src/antd'
import styled from 'styled-components'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import StyledModal, { ModalLoadContainer } from './style'
import { IModal } from './types'
import { useTxStrings } from '../../transifex'

const StyledAntdModal = styled(AntdModal)`
  ${StyledModal}
`

export type { IModal } from './types'

const Modal = ({
  children,
  visible,
  onClose,
  disableSubmitButton = false,
  onSubmit = () => null,
  variant,
  loading,
  fullscreen,
  width,
  styles,
  modalStyles,
  ...restProps
}: IModal): JSX.Element => {
  const { txLoading } = useTxStrings()

  // FULLSCREEN IS RENDERING-SPECIFIC, so the wrapper stops emitting one
  // shape for both. antd's `.ant-modal` root is top-anchored and statically
  // positioned, so top/left/max-width/padding on it is exactly right — that
  // branch is untouched. The shadcn panel is `fixed` and centred with a
  // -50%/-50% translate, so the same inline styles would leave the translate
  // in place and push the panel half off-screen (up and left). Its fullscreen
  // is expressed in `./style.ts` instead, on the `[data-slot='dialog-content']`
  // twin, which can undo the translate and the max-height clamp that an
  // inline style cannot reach.
  const isAntd = useDesignMode() === 'antd'
  const antdFullscreen = fullscreen && isAntd
  const fullscreenWidth = isAntd ? '100%' : undefined
  const resolvedWidth = fullscreen ? fullscreenWidth : width

  return (
    <StyledAntdModal
      $styles={styles}
      styles={modalStyles}
      $isFullScreen={fullscreen}
      $isWithFooter={!!restProps.footer}
      style={
        antdFullscreen
          ? {
              top: '0%',
              left: '0%',
              maxWidth: '100%',
              padding: 0,
            }
          : {}
      }
      $variant={variant}
      open={visible}
      onCancel={onClose}
      onOk={onSubmit}
      okButtonProps={{ disabled: disableSubmitButton }}
      width={resolvedWidth}
      {...restProps}
    >
      {loading ? (
        <>
          <ModalLoadContainer>
            <Space size="middle">
              <Spin size="large" tip={txLoading} />
            </Space>
          </ModalLoadContainer>
        </>
      ) : (
        children
      )}
    </StyledAntdModal>
  )
}

export default Modal
